import { useEffect } from 'react'
import { Check, X, Users } from 'lucide-react'
import { useWaitingRoomStore } from '@os/spaces/web/stores/waitingRoomStore'
import { getSocket } from '../../services/socket'
import { useRecordingStore } from '@os/spaces/web/stores/recordingStore'
import { useAuthStore } from '../../stores/authStore'
import { useVoiceStore } from '../../stores/voiceStore'
import { mediaUrl } from '../../services/config'
import { displayInitial } from '../../utils/displayInitial'

interface Props {
  channelId: string
}

const EMPTY_PENDING: ReadonlyArray<{ waitingId: string; channelId: string; userId: string; username: string; avatarUrl: string | null; requestedAt: string }> = []

/**
 * Floating panel visible to hosts that lists pending waiting-room
 * requests. Approve / Reject emit socket events; the user is notified
 * via voice:waiting:approved / rejected.
 *
 * Hidden when there are no pending requests, or the caller is not host.
 */
export default function WaitingRequestsPanel({ channelId }: Props) {
  const isHost = useRecordingStore((s) => !!s.isHost[channelId])
  const pending = useWaitingRoomStore((s) => s.pending[channelId]) ?? EMPTY_PENDING
  const setPending = useWaitingRoomStore((s) => s.setPending)
  const resolveRequest = useWaitingRoomStore((s) => s.resolveRequest)

  // Pull current pending list once when the panel mounts.
  useEffect(() => {
    if (!isHost || !channelId) return
    const socket = getSocket()
    socket.emit('voice:waiting:list', { channelId }, (resp: any) => {
      if (resp?.entries) {
        setPending(channelId, resp.entries.map((e: any) => ({
          waitingId: e.id,
          channelId: e.channelId,
          userId: e.userId,
          username: e.user?.username ?? 'Unknown',
          avatarUrl: e.user?.avatarUrl ?? null,
          requestedAt: e.requestedAt,
        })))
      }
    })
  }, [channelId, isHost, setPending])

  if (!isHost) return null
  if (!pending.length) return null

  // Drop the local row immediately so the panel hides on click. The server
  // still fans out voice:waiting:resolved to every host (us included) for
  // peer hosts; resolveRequest is idempotent so the second pass is a no-op.
  // Don't wait on the round-trip — when the broadcast is dropped (network
  // blip, multi-process Redis, late delivery after reload) the row used to
  // stick visible until the page was reloaded (OSPROD-44).
  const approve = (waitingId: string, isPhone?: boolean) => {
    resolveRequest(channelId, waitingId)
    getSocket().emit('voice:waiting:approve', { channelId, waitingId })
    // PSTN callers: the SIP gateway answers the phone leg only once it can
    // subscribe to a remote AUDIO track in this room, and a muted participant
    // publishes no track at all — admit = answer the phone, mic comes up live.
    // The panel also renders for hosts who merely VIEW the channel (isHost is
    // connection-independent): there, park the one-shot auto-join intent with
    // the 'unmute' policy — VoiceChannelView is mounted right behind this
    // panel and consumes it immediately.
    if (isPhone) {
      if (useVoiceStore.getState().currentChannelId === channelId) {
        useAuthStore.getState().unmuteForPhoneAdmit()
      } else {
        useVoiceStore.getState().setPendingAutoJoin(channelId, 'unmute')
      }
    }
  }
  const reject = (waitingId: string) => {
    resolveRequest(channelId, waitingId)
    getSocket().emit('voice:waiting:reject', { channelId, waitingId })
  }

  return (
    <div className="absolute top-3 right-3 z-30 w-72 rounded-xl bg-card border border-border shadow-xl overflow-hidden">
      <div className="px-3 py-2 border-b border-border flex items-center gap-2">
        <Users size={14} className="text-amber-500" />
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Waiting room · {pending.length}
        </span>
      </div>
      <div className="max-h-72 overflow-y-auto divide-y divide-border">
        {pending.map((p) => (
          <div key={p.waitingId} className="px-3 py-2 flex items-center gap-2">
            {p.avatarUrl ? (
              <img src={mediaUrl(p.avatarUrl)} alt={p.username} className="w-7 h-7 rounded-full object-cover" />
            ) : (
              <div className="w-7 h-7 rounded-full bg-muted text-foreground flex items-center justify-center text-xs">
                {displayInitial(p.username)}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-foreground truncate">{p.username}</div>
              <div className="text-[10px] text-muted-foreground">wants to join</div>
            </div>
            <button
              onClick={() => approve(p.waitingId, p.userId.startsWith('phone:'))}
              className="w-7 h-7 rounded-md bg-green-600 text-white hover:bg-green-700 flex items-center justify-center"
              title="Admit"
            >
              <Check size={14} />
            </button>
            <button
              onClick={() => reject(p.waitingId)}
              className="w-7 h-7 rounded-md bg-muted text-foreground hover:bg-destructive hover:text-destructive-foreground flex items-center justify-center"
              title="Reject"
            >
              <X size={14} />
            </button>
          </div>
        ))}
      </div>
    </div>
  )
}
